import React from 'react';
import type { InventoryItem } from '../../../../shared/CharacterTypes';
import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import { ItemIcon } from '../../components/ItemIcon';
import { Tooltip, createItemTooltip, type ItemTooltipViewerContext } from '../../components/Tooltip';
import { isTouchDevice } from '../../mobile/touchControlsFlag';
import { TomeButton } from '../../primitives/tome';
import { inventoryItemName, numericItemStats, readableItemId } from './InventoryPresentation';
import { InventoryDropControl } from './InventoryDropControl';

export function InventoryItemDetail({ item, viewer, activate, drop }: {
  item: InventoryItem | null; viewer?: ItemTooltipViewerContext; activate: (item: InventoryItem) => void;
  drop?: (item: InventoryItem, quantity: number) => void;
}): React.ReactElement {
  if (!item) return <section className="inventory-browser__detail inventory-browser__empty" aria-label="Selected bag item">
    <h3>Inspect your bag</h3><p>Select an item to see its effects, requirements and available action.</p>
  </section>;
  const tooltip = createItemTooltip({ ...item, name: inventoryItemName(item), stats: numericItemStats(item.stats) }, viewer, { actions: 'bag' });
  const known = ITEM_DATABASE[item.id] != null;
  const action = item.type === 'weapon' || item.type === 'armor' ? 'Equip' : item.type === 'consumable' ? 'Use' : null;
  const blocked = tooltip.requirements?.some(requirement => requirement.status !== 'met') ?? false;
  return <section className="inventory-browser__detail" aria-label="Selected bag item">
    <header className="inventory-browser__item-heading">
      <Tooltip data={tooltip}><div className="inventory-browser__art" tabIndex={0} aria-label={`${tooltip.title} item details`}>
        {known ? <ItemIcon itemId={item.id} size={58} /> : <span aria-label="Artwork unavailable">?</span>}
      </div></Tooltip>
      <div><p className="inventory-browser__eyebrow">Bag item · Quantity {item.quantity}</p>
        <h3>{tooltip.title}</h3><p>{tooltip.rarity ? `${tooltip.rarity[0].toUpperCase()}${tooltip.rarity.slice(1)}` : 'Rarity unavailable'}</p></div>
    </header>
    <div className="inventory-browser__item-reading">
      <p>{tooltip.description || 'No description available.'}</p>
      {!known && <p className="inventory-browser__muted">Item metadata unavailable · {readableItemId(item.id)}</p>}
      {tooltip.effects?.length ? <ul>{tooltip.effects.map(effect => <li key={effect}>{effect}</li>)}</ul> : null}
      {tooltip.stats?.length ? <dl className="inventory-browser__stats">{tooltip.stats.map(stat => <div key={stat.label}>
        <dt>{stat.label}</dt><dd>{typeof stat.value === 'number' && stat.value > 0 ? `+${stat.value}` : stat.value}</dd>
      </div>)}</dl> : null}
      {tooltip.requirements?.length ? <ul aria-label="Item requirements" className="inventory-browser__requirements">
        {tooltip.requirements.map(requirement => <li key={requirement.text} data-requirement-status={requirement.status}
          style={{ color: requirement.status === 'unmet' ? 'var(--tome-danger, #9b1f17)' : requirement.status === 'met' ? 'var(--tome-moss, #2f5a26)' : 'var(--tome-ink-muted, #6b5a43)' }}>{requirement.text}</li>)}
      </ul> : <p className="inventory-browser__muted">{known ? 'No special requirements' : 'Requirements unavailable'}</p>}
    </div>
    <footer className="inventory-browser__item-action">
      {action ? <TomeButton tone="primary" fullWidth disabled={blocked} onClick={() => activate(item)}
        aria-label={`${action} ${tooltip.title}`}>{action} {tooltip.title}</TomeButton>
        : <p className="inventory-browser__muted">Keep this item in your bag, or drag it to a supported destination.</p>}
      <small>{!action ? 'Only bag contents are shown here.' : isTouchDevice() ? 'Your bag updates after confirmation.'
        : 'Right-click remains a shortcut. Your bag updates after confirmation.'}</small>
      {drop && <InventoryDropControl key={item.id} item={item} name={tooltip.title} drop={drop} />}
    </footer>
  </section>;
}
